import { useCallback, useState } from "react";
import { APILoadingStatus, useApiLoadingStatus, __resetModuleState } from "@vis.gl/react-google-maps";
import { Button } from "@/components/ui/button";
import { MapPinOff, RotateCw } from "lucide-react";

// Google Maps load-state guard. The Maps JS script is fetched at runtime, so on
// a flaky connection (mobile/cellular) it can fail or hang, and vis.gl's <Map>
// then renders a dead blank box with no feedback and no way to recover (seen in
// production session replays). Wrap every <Map> surface in this guard, inside
// its <APIProvider>:
//   * NOT_LOADED / LOADING → pulsing placeholder (the box visibly "works")
//   * FAILED               → message + "Try again" (remounts the provider via
//                            useMapRetryKey; the loader re-attempts after a
//                            failure, FAILED doesn't latch)
//   * AUTH_FAILURE         → quiet "unavailable" note (retry won't fix config)
//   * LOADED               → children (the actual map)

// Recovering from a failed load needs care (all verified against v1.8.3):
//  • vis.gl keeps the loading status at module level, and after a FAILED load a
//    plain provider remount re-fetches the script via its "importLibrary
//    already exists" branch, which never flips the status back to LOADED, so
//    <Map> (gated on useApiIsLoaded) stays dead forever.
//  • Resetting the status first (__resetModuleState) doesn't work either: that
//    same branch then reports LOADED *before* the script arrives, and the
//    provider's own effects immediately dereference google.maps.Settings →
//    crash.
// So retry (a) re-fetches the API MANUALLY through the loader stub, the
// stub's internal promise is cleared when a script errors, so calling it
// injects a fresh <script> and resolves only when the API is genuinely ready,
// then (b) resets the module state and remounts the provider, whose LOADED is
// now truthful. `ready` (from APIProvider's onLoad) additionally gates the
// children as a belt-and-braces against premature LOADED.

/**
 * Wire-up for a retryable map surface:
 *   <APIProvider key={key} onLoad={onLoad} …>
 *     <MapLoadGuard retry={retry} ready={ready} retrying={retrying}>…</MapLoadGuard>
 *   </APIProvider>
 */
export const useMapRetryKey = () => {
  const [key, setKey] = useState(0);
  const [ready, setReady] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const onLoad = useCallback(() => setReady(true), []);
  const retry = useCallback(async () => {
    setRetrying(true);
    try {
      const gm = (
        window as unknown as {
          google?: { maps?: { Map?: unknown; importLibrary?: (name: string) => Promise<unknown> } };
        }
      ).google?.maps;
      if (gm?.importLibrary && !gm.Map) {
        // API never actually arrived (stub only), fetch it for real first.
        const importLibrary = gm.importLibrary;
        await Promise.all(["core", "maps", "marker"].map((l) => importLibrary(l)));
      }
      try {
        // Exported "for testing", but it is precisely the reset we need;
        // guarded in case a future version drops it.
        __resetModuleState?.();
      } catch {
        /* best-effort */
      }
      setReady(false);
      setKey((k) => k + 1);
    } catch {
      // Script failed again, fall back to the error state; user can retry.
    } finally {
      setRetrying(false);
    }
  }, []);
  return { key, retry, onLoad, ready, retrying };
};

const Shell = ({ children }: { children: React.ReactNode }) => (
  <div className="w-full h-full flex flex-col items-center justify-center gap-2.5 bg-muted/40 p-4 text-center">
    {children}
  </div>
);

const MapLoadGuard = ({
  retry,
  ready = true,
  retrying = false,
  children,
}: {
  /** From useMapRetryKey, omit to hide the retry button. */
  retry?: () => void;
  /** From useMapRetryKey (APIProvider onLoad fired). Defaults true for
   *  standalone use; always pass it when wiring retry. */
  ready?: boolean;
  /** From useMapRetryKey, a manual re-fetch is in flight. */
  retrying?: boolean;
  children: React.ReactNode;
}) => {
  const status = useApiLoadingStatus();

  if (retrying) {
    return <div className="w-full h-full bg-muted/50 animate-pulse" aria-hidden="true" />;
  }

  if (status === APILoadingStatus.FAILED) {
    return (
      <Shell>
        <MapPinOff className="h-6 w-6 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">
          The map couldn't load. Check your connection.
        </p>
        {retry && (
          <Button type="button" variant="outline" size="sm" onClick={retry} className="gap-1.5">
            <RotateCw className="h-3.5 w-3.5" /> Try again
          </Button>
        )}
      </Shell>
    );
  }

  if (status === APILoadingStatus.AUTH_FAILURE) {
    return (
      <Shell>
        <MapPinOff className="h-6 w-6 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">The map is temporarily unavailable.</p>
      </Shell>
    );
  }

  if (status !== APILoadingStatus.LOADED || !ready) {
    // NOT_LOADED / LOADING, script still on the wire.
    return <div className="w-full h-full bg-muted/50 animate-pulse" aria-hidden="true" />;
  }

  return <>{children}</>;
};

export default MapLoadGuard;
